import api, { withLoading } from '@/api/api';
import parseParams from "@/helpers/objectToQueryParams";

/**
 * Representa uma Budget Key (posição orçamentária)
 * 
 * @typedef {Object} BudgetKey
 * @property {number} id - Identificador único
 * @property {string} posicao - Nome da posição
 * @property {Object|null} participante - Participante associado
 * @property {Array} participantes - Lista de participantes com vigência
 * @property {Object|null} perfil - Perfil atual associado
 * @property {Array} perfis - Lista de perfis com vigência
 */

/**
 * Representa um perfil associado a uma Budget Key com vigência
 * 
 * @typedef {Object} BudgetKeyPerfil
 * @property {number} id - ID do perfil
 * @property {string} especialidade - Especialidade do perfil (pivot)
 * @property {string} senioridade - Senioridade do perfil (pivot)
 * @property {string|null} competencia_inicio - Data de início no formato "YYYY-MM" (pode ser nulo)
 * @property {string|null} competencia_fim - Data de fim no formato "YYYY-MM" (pode ser nulo)
 */

const resource = `v1/ativacaoped/:empresaId/budget-keys`;
const LOADING_KEY = 'ativacaoped-budget-keys';

/**
 * Lista budget keys com paginação e filtros.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @param {number} [params.page] - Número da página
 * @param {number} [params.per_page] - Itens por página
 * @returns {Promise<PaginacaoLaravelResource<BudgetKey>>} Lista paginada
 */
export const getBudgetKeys = (empresaId, params = { page: 1, per_page: 10 }) => {
  const query = parseParams(params);
  let url = resource.replace(":empresaId", empresaId);

  if (query) {
    url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data)
  );
};

/**
 * Lista budget keys simplificadas para componentes de seleção.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} [params] - Parâmetros de consulta
 * @returns {Promise<BudgetKey[]>} Lista de budget keys
 */
export const getSelecaoBudgetKeys = (empresaId, params = {}) => {
  const query = parseParams(params);
  let url = resource.replace(":empresaId", empresaId).concat(`/selecao`);

  if (query) {
    url.concat(`?${query}`);
  }

  return withLoading(LOADING_KEY, () =>
    api.get(url).then(({ data }) => data)
  );
};

/**
 * Cria uma nova budget key.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {Object} data - Dados da budget key
 * @param {string} data.posicao - Nome da posição (obrigatório)
 * @param {BudgetKeyPerfil[]} [data.perfis] - Lista de perfis associados
 * @returns {Promise<BudgetKey>} Budget key criada
 */
export const postBudgetKey = (empresaId, data) => {
  const baseUrl = resource.replace(":empresaId", empresaId);

  return withLoading(LOADING_KEY, () =>
    api.post(baseUrl, data).then(({ data }) => data.data)
  );
};

/**
 * Atualiza uma budget key existente.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} budgetKeyId - ID da budget key
 * @param {Object} data - Dados parciais para atualização
 * @param {string} [data.posicao] - Nome da posição
 * @param {BudgetKeyPerfil[]} [data.perfis] - Lista de perfis associados
 * @returns {Promise<BudgetKey>} Budget key atualizada
 */
export const putBudgetKey = (empresaId, budgetKeyId, data) => {
  const baseUrl = resource.replace(":empresaId", empresaId);
  return withLoading(LOADING_KEY, () =>
    api.post(`${baseUrl}/${budgetKeyId}`, data).then(({ data }) => data.data)
  );
};

/**
 * Exclui uma budget key.
 *
 * @param {number|string} empresaId - ID da empresa
 * @param {number|string} budgetKeyId - ID da budget key
 * @returns {Promise<void>}
 */
export const deleteBudgetKey = (empresaId, budgetKeyId) => {
  const baseUrl = resource.replace(":empresaId", empresaId);
  return withLoading(LOADING_KEY, () =>
    api.delete(`${baseUrl}/${budgetKeyId}`)
  );
};

export default {
  getBudgetKeys,
  getSelecaoBudgetKeys,
  postBudgetKey,
  putBudgetKey,
  deleteBudgetKey
};
